/**
 * Step 3: read-only route and final passenger/cargo assignment summary.
 *
 * @remarks
 * Route editing returns through the explicit Edit route action. Persistence remains
 * in the dialog footer and state hook, so this step performs no writes.
 *
 * @packageDocumentation
 */
import React from 'react';
import {Button, Icon, Typography} from 'antd';
import {AssignmentRow, assignmentKey} from '../../../../../../common/flight-leg/assignment-plan';
import AutoAssignRoutePreview from '../../shared/route-preview';
import JourneyEndpoint from '../../shared/journey-endpoint';
import type {AutoAssignReview} from '../../state/use-auto-assign-review';

/** Final projection, navigation action, and display names for summary rows. */
export interface FinalReviewStepProps {
    flow: Pick<AutoAssignReview, 'plan' | 'saving' | 'edit' | 'previewProps'>;
    names: Map<string, string>;
    labels: Map<string, string>;
    /** Hide when the originating action has no route editor to return to. */
    showEditRoute?: boolean;
}

/** Renders the final route and compact passenger/cargo summary without editing controls. */
export default function FinalReviewStep({
    flow,
    names,
    labels,
    showEditRoute = true,
}: FinalReviewStepProps) {
    const {plan, saving} = flow;
    const entityName = (row: AssignmentRow) => labels.get(assignmentKey(row)) || row.id;
    return (
        <>
            <div className="flow-section-title">
                <Typography.Title level={4}>Final review</Typography.Title>
                {showEditRoute && (
                    <Button disabled={saving} onClick={flow.edit}>
                        Edit route
                    </Button>
                )}
            </div>
            <AutoAssignRoutePreview {...flow.previewProps} />
            <div className="confirmed-choices">
                <Typography.Title level={4}>Assignment summary</Typography.Title>
                {(['pax', 'cgo'] as const)
                    .filter((kind) => plan.rows.some((row) => row.kind === kind))
                    .map((kind) => (
                        <div className="assignment-summary-panel" key={kind}>
                            <table className="assignment-summary-table">
                                <caption>
                                    <Icon type={kind === 'pax' ? 'user' : 'inbox'} />{' '}
                                    {kind === 'pax' ? 'Passengers' : 'Cargo'}
                                </caption>
                                <thead>
                                    <tr>
                                        <th scope="col">Name</th>
                                        <th scope="col">
                                            {kind === 'pax' ? 'Board at' : 'Load at'}
                                        </th>
                                        <th scope="col">
                                            {kind === 'pax' ? 'Leave at' : 'Unload at'}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {plan.rows
                                        .filter((row) => row.kind === kind)
                                        .flatMap((row) => {
                                            if (!row.ranges.length)
                                                return [
                                                    <tr key={assignmentKey(row)}>
                                                        <th scope="row">{entityName(row)}</th>
                                                        <td
                                                            colSpan={2}
                                                            className="summary-unassigned"
                                                        >
                                                            <Icon type="minus-circle" /> Unassigned
                                                        </td>
                                                    </tr>,
                                                ];
                                            return row.ranges.map((range, index) => (
                                                <tr key={`${assignmentKey(row)}:${index}`}>
                                                    <th scope="row">{entityName(row)}</th>
                                                    <td>
                                                        {
                                                            <JourneyEndpoint
                                                                plan={plan}
                                                                names={names}
                                                                visit={range.from}
                                                                kind={kind}
                                                                boarding
                                                                withVisit
                                                            />
                                                        }
                                                    </td>
                                                    <td>
                                                        {
                                                            <JourneyEndpoint
                                                                plan={plan}
                                                                names={names}
                                                                visit={range.to}
                                                                kind={kind}
                                                                boarding={false}
                                                                withVisit
                                                            />
                                                        }
                                                    </td>
                                                </tr>
                                            ));
                                        })}
                                </tbody>
                            </table>
                        </div>
                    ))}
            </div>
        </>
    );
}
